import {
    UsageEventType,
    UsageLogEvent,
    UsageLogTypes
} from '@/constants/usage';
import { AppApi } from '@/customApiClients/appApiClient/Api';
import { useCallback } from 'react';

import { useTrackUsage } from '@/hooks/usage/useTrackUsage';

interface FolderData {
    id: string;
    name: string;
    level: number;
    parentId?: string | null;
    expanded: boolean;
}

interface ProjectManagementProps {
    appApiClient: AppApi<any>;
    folders: FolderData[];
    setFolders: React.Dispatch<React.SetStateAction<FolderData[]>>;
    loadFolders: () => Promise<void>;
    assignThreadToFolder: (
        folderId: string,
        threadId: string,
        skipReload?: boolean
    ) => Promise<void>;
}

export function useProjectManagement({
    appApiClient,
    folders,
    setFolders,
    loadFolders,
    assignThreadToFolder
}: ProjectManagementProps) {
    const { mutate: trackUsage } = useTrackUsage();

    const createFolder = useCallback(
        async (name: string, threadId?: string) => {
            if (!name.trim()) return;

            try {
                console.log('Creating folder:', name.trim());
                console.log('Thread to assign after creation:', threadId);

                const response =
                    await appApiClient.project.createFolderProjectFoldersPost({
                        name: name.trim()
                    });

                console.log('Folder creation response:', response);

                trackUsage({
                    subject: UsageLogTypes.ProjectFolder,
                    action: UsageLogEvent.Added,
                    type: UsageEventType.Usage,
                    properties: {
                        folderName: name.trim(),
                        folderId: response.data?.id || ''
                    }
                }).catch((err) =>
                    console.error(
                        '[useProjectManagement] Failed to track folder creation:',
                        err
                    )
                );

                // If there's a thread waiting to be assigned, assign it to the new folder
                if (threadId && response.data?.id) {
                    const folderId = response.data.id;
                    console.log(
                        'Assigning thread',
                        threadId,
                        'to folder',
                        folderId
                    );

                    try {
                        await assignThreadToFolder(folderId, threadId, true);
                        console.log('Successfully assigned thread to folder');
                        await loadFolders();
                    } catch (assignError) {
                        console.error(
                            'Failed to assign thread to new folder:',
                            assignError
                        );
                        await loadFolders();
                    }
                } else {
                    console.log('No thread to assign after folder creation');
                    await loadFolders();
                }
            } catch (err) {
                console.error('Failed to create folder:', err);
            }
        },
        [appApiClient, assignThreadToFolder, loadFolders, trackUsage]
    );

    const createSubfolder = useCallback(
        async (parentId: string, name: string) => {
            if (!parentId || !name.trim()) return;

            try {
                console.log(
                    'Creating subfolder:',
                    name.trim(),
                    'under parent:',
                    parentId
                );
                await appApiClient.project.createFolderProjectFoldersPost({
                    name: name.trim(),
                    parent_folder_id: parentId
                });
                await loadFolders();
            } catch (err) {
                console.error('Failed to create subfolder:', err);
            }
        },
        [appApiClient, loadFolders]
    );

    const renameFolder = useCallback(
        async (folderId: string, newName: string) => {
            if (!folderId || !newName.trim()) return;

            try {
                await appApiClient.project.updateFolderProjectFoldersPut({
                    folder_id: folderId,
                    name: newName.trim()
                });
                await loadFolders();
            } catch (err) {
                console.error('Failed to rename folder:', err);
            }
        },
        [appApiClient, loadFolders]
    );

    const deleteFolder = useCallback(
        async (folderId: string) => {
            if (!folderId) return;

            try {
                await appApiClient.project.deleteFolderProjectFoldersDelete({
                    folder_id: folderId
                });
                await loadFolders();
            } catch (err) {
                console.error('Failed to delete folder:', err);
            }
        },
        [appApiClient, loadFolders]
    );

    const moveFolder = useCallback(
        async (folderId: string, targetParentId: string | null) => {
            if (!folderId) return;

            const folderToMove = folders?.find((f) => f.id === folderId);
            if (!folderToMove) return;

            try {
                await appApiClient.project.updateFolderProjectFoldersPut({
                    folder_id: folderId,
                    name: folderToMove.name,
                    parent_id: targetParentId,
                    expanded: folderToMove.expanded
                });
                await loadFolders();
            } catch (err) {
                console.error('Failed to move folder:', err);
            }
        },
        [appApiClient, folders, loadFolders]
    );

    const toggleFolder = useCallback(
        async (folderId: string) => {
            // First update the UI optimistically
            let newExpandedState: boolean;
            setFolders((prev: FolderData[]) =>
                prev.map((folder: FolderData) => {
                    if (folder.id === folderId) {
                        newExpandedState = !folder.expanded;
                        return { ...folder, expanded: newExpandedState };
                    }
                    return folder;
                })
            );

            // Then persist to backend
            try {
                const folder = folders.find((f) => f.id === folderId);
                if (folder) {
                    await appApiClient.project.updateFolderProjectFoldersPut({
                        folder_id: folderId,
                        name: folder.name,
                        parent_id: folder.parentId,
                        expanded: newExpandedState!
                    });
                }
            } catch (err) {
                console.error('Failed to update folder expanded state:', err);
                // Revert the UI change on error
                setFolders((prev: FolderData[]) =>
                    prev.map((folder: FolderData) =>
                        folder.id === folderId
                            ? { ...folder, expanded: !newExpandedState! }
                            : folder
                    )
                );
            }
        },
        [appApiClient, folders, setFolders]
    );

    const moveFolderToParent = useCallback(
        async (folderId: string, newParentId: string) => {
            try {
                const folder = folders.find((f) => f.id === folderId);
                if (!folder) return;

                // Prevent moving a folder into its current parent (redundant operation)
                if (folder.parentId === newParentId) {
                    return;
                }

                // Prevent moving a folder into its own descendant (circular reference)
                const isDescendantFolder = (
                    parentId: string,
                    potentialChildId: string
                ): boolean => {
                    if (parentId === potentialChildId) return true;

                    const checkDescendants = (folderId: string): boolean => {
                        const children = folders.filter(
                            (f) => f.parentId === folderId
                        );
                        for (const child of children) {
                            if (child.id === potentialChildId) return true;
                            if (checkDescendants(child.id)) return true;
                        }
                        return false;
                    };

                    return checkDescendants(parentId);
                };

                if (isDescendantFolder(folderId, newParentId)) {
                    return;
                }

                await appApiClient.project.updateFolderProjectFoldersPut({
                    folder_id: folderId,
                    name: folder.name,
                    parent_id: newParentId,
                    expanded: folder.expanded
                });

                await loadFolders();
            } catch (err) {
                console.error('Failed to move folder:', err);
            }
        },
        [appApiClient, folders, loadFolders]
    );

    const moveFolderToRoot = useCallback(
        async (folderId: string) => {
            try {
                const folder = folders.find((f) => f.id === folderId);
                if (!folder) return;

                await appApiClient.project.updateFolderProjectFoldersPut({
                    folder_id: folderId,
                    name: folder.name,
                    parent_id: null, // Explicitly null for root level
                    expanded: folder.expanded
                });

                await loadFolders();
            } catch (err) {
                console.error('Failed to move folder to root:', err);
            }
        },
        [appApiClient, folders, loadFolders]
    );

    return {
        createFolder,
        createSubfolder,
        renameFolder,
        deleteFolder,
        moveFolder,
        toggleFolder,
        moveFolderToParent,
        moveFolderToRoot
    };
}
